import { For, ParentProps, Show, createContext, createEffect, createSignal, on } from 'solid-js'
import styles from './st-query-results.module.css'
import Toggle from '../st-toggle/st-toggle'
import Button from '../st-button/st-button'
import { QueryResultsContext } from './QueryResultsContext'
import { DataGrid, PivotGrid, PivotGridFieldChooser } from 'dx-app'
import TabGroup from '../st-tab-group/st-tab-group'
import Tab from '../st-tab/st-tab'

import { requireContext } from 'common'
import { STComponentWrapperProps } from '../st-component-wrapper/st-component-wrapper'
import DataStore from '../st-data-store/st-data-store'
import { showModal } from '../st-modal/st-modal'
import { ColumnMetadata } from 'st-app/api'

const QueryResultsContextJsx = createContext<QueryResultsContext>();
export const requireQueryResultsContext = () => {
    return requireContext(QueryResultsContextJsx);
}

export class QueryResultsProps extends STComponentWrapperProps {
}

export default function QueryResults(props: ParentProps<QueryResultsProps>) {
    const context = new QueryResultsContext();

    return (
        <QueryResultsContextJsx.Provider value={context}>
            <QueryResultsInternal {...props} />
        </QueryResultsContextJsx.Provider>
    )
}

function QueryResultsInternal(props: ParentProps<QueryResultsProps>) {
    const context = requireQueryResultsContext();

    const [isInAdvancedMode, setIsInAdvancedMode] = createSignal(false);
    const [copySuccess, setCopySuccess] = createSignal(false);

    createEffect(on(() => props.value, () => {
        if (props.value && props.value.length > 0) {
            context.load(props.value);
        }
    }))

    const copyToClipboard = async () => {
        const sql = context.sqlStatement();
        if (!sql) return;
        try {
            await navigator.clipboard.writeText(sql);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch {
            // Fallback for environments without clipboard API
            const el = document.createElement('textarea');
            el.value = sql;
            el.style.position = 'fixed';
            el.style.opacity = '0';
            document.body.appendChild(el);
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        }
    };

    return (
        <DataStore dataSource='id'>
            <div class={styles['result-container']}>
                <TabGroup>
                    <Tab id="result" label="Result">
                        <div class={styles['result-tab-content']}>
                            <div class={styles['result-tab-toolbar']}>
                                <Toggle value={[isInAdvancedMode, setIsInAdvancedMode]} label="Advanced Mode" />
                            </div>
                            <Show when={isInAdvancedMode()}>
                                <AdvancedMode />
                            </Show>
                            <Show when={!!context.errorMessage() && !context.loading()}>
                                <div class={styles['error-message']}>{context.errorMessage()}</div>
                            </Show>
                            <Show when={context.noDataFound() && !context.loading()}>
                                <div class={styles['no-data-message']}>No Data Found</div>
                            </Show>
                            <Show when={!isInAdvancedMode() && !context.noDataFound()}>
                                <DataGrid
                                    loading={context.loading()}
                                    showBorders={true}
                                    showRowLines={true}
                                    allowColumnReordering={true}
                                    allowColumnResizing={true}
                                    columnAutoWidth={true}
                                    scrolling={{
                                        showScrollbar: 'always',
                                    }}
                                    headerFilter={{
                                        visible: true,
                                        search: {
                                            enabled: true,
                                        }
                                    }}
                                    dataSource={context.store}
                                    paging={{
                                        pageSize: import.meta.env.DEV ? 10 : 20,
                                    }}
                                    pager={{
                                        showPageSizeSelector: true,
                                        allowedPageSizes: [10, 20, 50, 100],
                                        showInfo: true,
                                        showNavigationButtons: true,
                                    }}
                                    export={{
                                        enabled: true,
                                    }}
                                >
                                    <DataGrid.Toolbar visible>
                                        <DataGrid.PredefinedToolbarItem name='exportButton' />
                                    </DataGrid.Toolbar>
                                    <For each={context.columnMetadata()}>{metadata => (
                                        <DataGrid.Column
                                            dataField={metadata.columnName!}
                                            caption={metadata.columnLabel}
                                            cellTemplate={metadata.isPhoto ? (container, options) => {
                                                if (options.value) {
                                                    const img = document.createElement('img');
                                                    img.src = options.value;
                                                    img.style.height = '60px';
                                                    container.get(0)?.appendChild(img);
                                                }
                                            } : undefined}
                                        />
                                    )}</For>
                                </DataGrid>
                            </Show>
                        </div>
                    </Tab>
                    <Show when={!!context.sqlStatement()}>
                        <Tab id="sql-statement" label="SQL Statement">
                            <div class={styles['sql-tab-content']}>
                                <div class={styles['sql-tab-toolbar']}>
                                    <Button
                                        icon={copySuccess() ? 'fa fa-check' : 'fa fa-clipboard'}
                                        tooltip={copySuccess() ? 'Copied!' : 'Copy to clipboard'}
                                        onClick={copyToClipboard}
                                    >
                                        {copySuccess() ? 'Copied!' : 'Copy'}
                                    </Button>
                                </div>
                                <textarea
                                    class={styles['sql-textarea']}
                                    readOnly
                                    value={context.sqlStatement()}
                                    rows={12}
                                />
                            </div>
                        </Tab>
                    </Show>
                </TabGroup>
            </div>
        </DataStore>
    );
}

const AdvancedMode = () => {
    const context = requireQueryResultsContext();

    return (
        <>
            <div class={styles['advanced-mode-container']}>
                <PivotGrid
                    showBorders={true}
                    fieldPanel={{
                        visible: true,
                    }}
                    export={{
                        enabled: true,
                    }}
                    fieldChooser={{
                        enabled: false,
                        layout: 2,
                    }}
                    loadPanel={{ showPane: false, text: '' }}
                    dataSource={context.pivotDataSource}
                    onCellClick={(e) => {
                        if (e.area !== 'data') return;
                        if (!e.cell) return;

                        const dataSource = e.component.getDataSource();
                        const drillDownDataSource = dataSource.createDrillDownDataSource(e.cell);
                        drillDownDataSource.paginate(true);
                        drillDownDataSource.pageIndex(0);
                        drillDownDataSource.pageSize(10);

                        const metadatas = context.columnMetadata();
                        const hasScrollFields = metadatas.some(m => m.isScrollProperty);

                        const isPriority = (field: ColumnMetadata) => {
                            if (e.dataFields?.some(f => f.dataField === field.columnName)) return true;
                            if (e.columnFields?.some(f => f.dataField === field.columnName)) return true;
                            if (e.rowFields?.some(f => f.dataField === field.columnName)) return true;
                            if (hasScrollFields && field.isScrollProperty) return true;
                            return false;
                        }


                        showModal(() => {
                            const [showAll, setShowAll] = createSignal(!hasScrollFields);

                            return (
                                <div style={{ width: '80vw', height: '100%', 'max-height': '80vh', 'overflow-y': 'auto' }}>
                                    <DataGrid
                                        columnHidingEnabled={true}
                                        dataSource={drillDownDataSource}
                                        showBorders={true}
                                        loadPanel={{
                                            enabled: true,
                                        }}
                                        export={{
                                            enabled: true
                                        }}
                                    >
                                        <DataGrid.Toolbar visible>
                                            <DataGrid.PredefinedToolbarItem name='exportButton' />
                                            <DataGrid.ToolbarItem name='toggle-show-all'>
                                                <Toggle disabled={!hasScrollFields} value={[showAll, setShowAll]} label="Show All Fields" />
                                            </DataGrid.ToolbarItem>
                                        </DataGrid.Toolbar>

                                        <For each={metadatas}>{metadata => (
                                            <DataGrid.Column
                                                dataField={metadata.columnName!}
                                                caption={metadata.columnLabel}
                                                visible={showAll() || isPriority(metadata)}
                                                cellTemplate={metadata.isPhoto ? (container, options) => {
                                                    if (options.value) {
                                                        const img = document.createElement('img');
                                                        img.src = options.value;
                                                        img.style.height = '60px';
                                                        container.get(0)?.appendChild(img);
                                                    }
                                                } : undefined}
                                            />
                                        )}</For>
                                    </DataGrid>
                                </div>
                            )
                        }, {
                            title: 'Drill Down Data',
                            draggable: true,
                        });
                    }}
                />

                <PivotGridFieldChooser
                    dataSource={context.pivotDataSource}
                    allowSearch
                    condensed
                />
            </div>
        </>
    )
}

QueryResults.defaultProps = new QueryResultsProps();
